import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import { useBlockGroupContext } from "components/form/Blocks";

import { useSaveActionsContext } from "context/save";

import useSectionTitleSeparatorApi from "hooks/api/private/blocks/useSectionTitleSeparatorApi";
import { ButtonDeleteInterface } from "hooks/useTitleFunctionsTopBlock";

import {
    SectionTitleSeparatorBody,
    SectionTitleSeparatorDetails,
} from "types/api/blocks/sectionTitleSeparator";
import { LanguageEnum } from "types/api/commons";

export type SectionTitleSeparatorForm = {
    id: string | undefined;
    text: {
        [x in LanguageEnum]: {
            text: string;
        };
    };
};

type SectionTitleSeparatorType = {
    buttonDelete: ButtonDeleteInterface;
    formHook: FormHookType<SectionTitleSeparatorForm>;
};

function useSectionTitleSeparator(): SectionTitleSeparatorType {
    const { saveSectionTitleSeparator, getOneSectionTitleSeparators, deleteSectionTitleSeparator } =
        useSectionTitleSeparatorApi();

    const { blockGroupCompositionId, blockGroupId, blockId, graphicalOrder } =
        useBlockGroupContext();

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                id: {
                    type: "simple",
                    validators: [],
                },
                text: {
                    type: "collection",
                    of: {
                        type: "object",
                        properties: {
                            text: {
                                type: "simple",
                                validators: [notEmptyValidator],
                            },
                        },
                    },
                },
            },
        };
    }, []);

    const initialValues: SectionTitleSeparatorForm = React.useMemo(() => {
        return {
            id: undefined,
            text: {
                it: {
                    text: "",
                },
                en: {
                    text: "",
                },
            },
        };
    }, []);

    const formHook = useForm<SectionTitleSeparatorForm>(schema, initialValues);

    const { showAllError, setForm, isValid, form } = formHook;

    const [sectionTitleSeparatorDetails, setSectionTitleSeparatorDetails] = React.useState<
        SectionTitleSeparatorDetails | undefined
    >(undefined);

    React.useEffect(() => {
        const [apiCall, abortController] = getOneSectionTitleSeparators(
            { id: blockGroupCompositionId },
            { rejectNotFound: true }
        );

        apiCall.then(setSectionTitleSeparatorDetails).catch((error) => console.log(error));

        return () => abortController.abort();
    }, [setSectionTitleSeparatorDetails, getOneSectionTitleSeparators, blockGroupCompositionId]);

    React.useEffect(() => {
        if (sectionTitleSeparatorDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = sectionTitleSeparatorDetails.id;
                newForm.text = sectionTitleSeparatorDetails.text;

                return newForm;
            });
        }
    }, [setForm, sectionTitleSeparatorDetails]);

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction(blockGroupCompositionId, () => {
            if (isValid) {
                const promises: Array<Promise<unknown>> = [];

                const body: SectionTitleSeparatorBody = {
                    id: blockGroupCompositionId,
                    text: {
                        it: {
                            text: form.text.it.text,
                        },
                        en: {
                            text: form.text.en.text,
                        },
                    },
                };

                const [apiCall] = saveSectionTitleSeparator(body);

                promises.push(apiCall);

                return Promise.all(promises);
            } else {
                showAllError();

                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        saveSectionTitleSeparator,
        showAllError,
        addSaveAction,
        blockGroupCompositionId,
        graphicalOrder,
        blockGroupId,
        isValid,
        blockId,
        form,
    ]);

    return React.useMemo(
        (): SectionTitleSeparatorType => ({
            buttonDelete: deleteSectionTitleSeparator,
            formHook,
        }),
        [deleteSectionTitleSeparator, formHook]
    );
}

export default useSectionTitleSeparator;
